/**
 * ImportTemplateModal — importa um template de projeto a partir de JSON.
 *
 * Aceita colar o JSON ou enviar um arquivo .json no formato portátil exportado
 * pelo sistema: { label, descricao?, columns: [...], tasks: [{titulo, coluna}] }.
 * Valida a estrutura antes de salvar (pjImportTemplate). Estrutura pensada p/
 * evoluir para biblioteca/compartilhamento (o mesmo JSON serve de export/import).
 */

import { useState } from 'react'
import { useApp } from '../../context/AppContext.jsx'
import Icon from '../../components/icons.jsx'
import { pjImportTemplate } from '../../db/api.js'

const EXEMPLO = `{
  "label": "Meu fluxo",
  "descricao": "Opcional",
  "columns": ["A Fazer", "Em Andamento", "Concluído"],
  "tasks": [
    { "titulo": "Primeira tarefa", "coluna": 0 }
  ]
}`

export default function ImportTemplateModal({ onClose, onImported }) {
  const { toast } = useApp()
  const [text, setText] = useState('')
  const [isShared, setIsShared] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const onFile = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    try { setText(await f.text()); setErr(null) }
    catch { setErr('Não foi possível ler o arquivo.') }
  }

  const parseAndValidate = () => {
    let obj
    try { obj = JSON.parse(text) } catch { throw new Error('JSON inválido.') }
    const label = (obj.label || '').trim()
    if (!label) throw new Error('Campo "label" (nome do template) é obrigatório.')
    if (!Array.isArray(obj.columns) || !obj.columns.length)
      throw new Error('Campo "columns" deve ter ao menos uma coluna.')
    const columns = obj.columns.map((c) => String(c).trim()).filter(Boolean)
    if (!columns.length) throw new Error('Colunas inválidas.')
    const tasks = Array.isArray(obj.tasks)
      ? obj.tasks
          .filter((t) => t && t.titulo)
          .map((t) => ({ titulo: String(t.titulo), coluna: Number(t.coluna) || 0 }))
      : []
    return { label, descricao: obj.descricao || null, columns, tasks }
  }

  const submit = async () => {
    if (saving) return
    setErr(null)
    let data
    try { data = parseAndValidate() } catch (e) { setErr(e.message); return }
    setSaving(true)
    try {
      const t = await pjImportTemplate({ ...data, is_shared: isShared, origin: 'imported' })
      toast('Template importado.', 'success')
      onImported?.(t)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <div style={S.overlay} onMouseDown={onClose}>
      <div style={S.box} onMouseDown={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <h4 style={S.title}>Importar template</h4>
          <button style={S.x} onClick={onClose}>×</button>
        </div>
        {err && <div style={S.error}>{err}</div>}

        <div style={S.rowBetween}>
          <span style={S.miniLabel}>Cole o JSON do template</span>
          <label style={S.fileBtn}>
            <Icon n="download" s={13} /> Enviar .json
            <input type="file" accept="application/json,.json" hidden onChange={onFile} />
          </label>
        </div>
        <textarea style={S.textarea} value={text} placeholder={EXEMPLO}
          onChange={(e) => setText(e.target.value)} />

        <label style={S.checkRow}>
          <input style={S.checkbox} type="checkbox" checked={isShared} onChange={(e) => setIsShared(e.target.checked)} />
          <span>Compartilhar com toda a organização</span>
        </label>

        <div style={S.footer}>
          <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
          <button style={S.btnPrimary} onClick={submit} disabled={saving || !text.trim()}>
            {saving ? 'Importando…' : 'Importar'}
          </button>
        </div>
      </div>
    </div>
  )
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(16,24,40,.35)', zIndex: 1300,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  box: {
    width: 560, maxWidth: '100%', background: 'var(--surface)', border: '1px solid #eaecf0',
    borderRadius: 14, padding: 20, boxShadow: '0 24px 48px rgba(16,24,40,.24)',
    display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '86vh', overflowY: 'auto',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: '#101828' },
  x: { border: 'none', background: 'transparent', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: '#667085', fontSize: 20, lineHeight: 1 },
  rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  miniLabel: { fontSize: 13, fontWeight: 500, color: '#344054' },
  fileBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, border: '1px solid #d0d5dd',
    background: 'var(--surface)', color: '#344054', borderRadius: 8, padding: '0 10px',
    fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', minHeight: 200, resize: 'vertical', padding: '10px 12px',
    borderRadius: 8, border: '1px solid #d0d5dd', background: 'var(--surface)', color: '#101828',
    fontSize: 12.5, lineHeight: 1.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#344054' },
  checkbox: { width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer', margin: 0 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid #eaecf0' },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: 8,
    border: '1px solid var(--blue)', background: 'var(--blue)', color: '#fff',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: 8,
    border: '1px solid #d0d5dd', background: 'var(--surface)', color: '#344054',
    fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  },
  error: {
    padding: '10px 14px', borderRadius: 10, background: '#fef6f5',
    border: '1px solid #fbd9d3', color: '#b42318', fontSize: 13,
  },
}
